import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiJson, getToken } from '../api/client.js';
import { FormInput } from '../components/FormControls.jsx';
import { FormRichTextarea } from '../components/FormRichTextarea.jsx';
import { Modal } from '../components/Modal.jsx';
import { formatDate, formatDateTime } from '../utils/formatDate.js';
import { clientLabel } from '../utils/clientLabel.js';
import { SearchableSelect } from '../components/SearchableSelect.jsx';

const TABS = ['clients', 'team', 'notifications', 'history'];

const TAB_LABELS = {
  clients: 'Clients',
  team: 'Team sync',
  notifications: 'Notifications',
  history: 'Email history',
};

export function AdminTimeTrackerPage() {
  const [searchParams] = useSearchParams();
  const nearLimitOnly = searchParams.get('near_limit') === '1';
  const [tab, setTab] = useState('clients');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [qbStatus, setQbStatus] = useState({ connected: false });
  const [syncing, setSyncing] = useState(false);

  const [settings, setSettings] = useState(null);
  const [mailgunOk, setMailgunOk] = useState(false);

  const [history, setHistory] = useState({ data: [], total: 0 });
  const [historySummary, setHistorySummary] = useState({ total: 0, sent: 0, failed: 0 });
  const [timeHistoryClient, setTimeHistoryClient] = useState(null);
  const [teamSync, setTeamSync] = useState({ employees: [], team_members: [], quickbooks_connected: false });
  const [loadingTeamSync, setLoadingTeamSync] = useState(false);

  const flash = useCallback((text, isError = false) => {
    if (isError) {
      setError(text);
      setMessage('');
    } else {
      setMessage(text);
      setError('');
    }
  }, []);

  const loadClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const [list, status] = await Promise.all([
        apiJson('/api/admin/time-tracker/clients'),
        apiJson('/api/admin/time-tracker/quickbooks/status'),
      ]);
      setClients(list);
      setQbStatus(status);
    } catch (e) {
      flash(e.message, true);
    } finally {
      setLoadingClients(false);
    }
  }, [flash]);

  const loadSettings = useCallback(async () => {
    try {
      const [s, mail] = await Promise.all([
        apiJson('/api/admin/time-tracker/notifications/settings'),
        apiJson('/api/admin/time-tracker/notifications/mail-status'),
      ]);
      setSettings(s);
      setMailgunOk(mail.configured);
    } catch (e) {
      flash(e.message, true);
    }
  }, [flash]);

  const loadHistory = useCallback(async () => {
    try {
      const [page, summary] = await Promise.all([
        apiJson('/api/admin/time-tracker/email-history?per_page=25'),
        apiJson('/api/admin/time-tracker/email-history/summary'),
      ]);
      setHistory(page);
      setHistorySummary(summary);
    } catch (e) {
      flash(e.message, true);
    }
  }, [flash]);

  const loadTeamSync = useCallback(async () => {
    setLoadingTeamSync(true);
    try {
      const data = await apiJson('/api/admin/time-tracker/employees');
      setTeamSync(data);
    } catch (e) {
      flash(e.message, true);
    } finally {
      setLoadingTeamSync(false);
    }
  }, [flash]);

  useEffect(() => {
    if (searchParams.get('quickbooks_connected') === '1') {
      flash('QuickBooks connected successfully.');
    }
    const qbErr = searchParams.get('quickbooks_error');
    if (qbErr) {
      flash(decodeURIComponent(qbErr), true);
    }
  }, [searchParams, flash]);

  useEffect(() => {
    if (tab === 'clients') {
      void loadClients();
    } else if (tab === 'team') {
      void loadTeamSync();
    } else if (tab === 'notifications') {
      void loadSettings();
    } else {
      void loadHistory();
    }
  }, [tab, loadClients, loadTeamSync, loadSettings, loadHistory]);

  const connectQuickBooks = async () => {
    try {
      const { auth_url: authUrl } = await apiJson('/api/admin/time-tracker/quickbooks/connect', {
        method: 'POST',
      });
      window.location.href = authUrl;
    } catch (e) {
      flash(e.message, true);
    }
  };

  const syncCustomers = async () => {
    setSyncing(true);
    try {
      const res = await apiJson('/api/admin/time-tracker/quickbooks/sync-customers', { method: 'POST' });
      flash(`Clients synced: ${res.stats.created} created, ${res.stats.updated} updated.`);
      await loadClients();
    } catch (e) {
      flash(e.message, true);
    } finally {
      setSyncing(false);
    }
  };

  const syncAllTime = async () => {
    setSyncing(true);
    try {
      const res = await apiJson('/api/admin/time-tracker/quickbooks/sync-all-time', { method: 'POST' });
      flash(`Time synced: ${res.stats.synced} new, ${res.stats.updated} updated.`);
      await loadClients();
    } catch (e) {
      flash(e.message, true);
    } finally {
      setSyncing(false);
    }
  };

  const saveClientLimit = async (client, timeLimit, autoResetDate) => {
    try {
      await apiJson(`/api/admin/time-tracker/clients/${client.id}/limit`, {
        method: 'PUT',
        body: { time_limit: Number(timeLimit), auto_reset_date: autoResetDate || '01-01' },
      });
      flash(`Saved limits for ${clientLabel(client)}.`);
      await loadClients();
    } catch (e) {
      flash(e.message, true);
    }
  };

  const resetClientTime = async (client) => {
    if (!window.confirm(`Reset tracked time for ${clientLabel(client)}?`)) {
      return;
    }
    try {
      await apiJson(`/api/admin/time-tracker/clients/${client.id}/reset-time`, { method: 'POST' });
      flash(`Time reset for ${clientLabel(client)}.`);
      await loadClients();
    } catch (e) {
      flash(e.message, true);
    }
  };

  const toggleNotifications = async (client, enabled) => {
    try {
      await apiJson(`/api/admin/time-tracker/clients/${client.id}/notifications`, {
        method: 'PUT',
        body: { notifications_enabled: enabled },
      });
      await loadClients();
    } catch (e) {
      flash(e.message, true);
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      await apiJson('/api/admin/time-tracker/notifications/settings', {
        method: 'POST',
        body: settings,
      });
      flash('Notification settings saved.');
    } catch (err) {
      flash(err.message, true);
    }
  };

  const testMailgun = async () => {
    try {
      await apiJson('/api/admin/time-tracker/notifications/test-mail', { method: 'POST' });
      flash('Test email sent.');
    } catch (e) {
      flash(e.message, true);
    }
  };

  const runJob = async (path, label) => {
    setSyncing(true);
    try {
      await apiJson(path, { method: 'POST' });
      flash(label);
      if (tab === 'clients') {
        await loadClients();
      }
    } catch (e) {
      flash(e.message, true);
    } finally {
      setSyncing(false);
    }
  };

  const cleanupHistory = async () => {
    if (!window.confirm('Delete email history older than 90 days?')) {
      return;
    }
    try {
      const res = await apiJson('/api/admin/time-tracker/email-history/cleanup?days=90', {
        method: 'DELETE',
      });
      flash(`Removed ${res.deleted} records.`);
      await loadHistory();
    } catch (e) {
      flash(e.message, true);
    }
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }} data-tour="page-title">Time tracker</h2>
      <p className="muted">
        Prepaid client hours, QuickBooks time sync, and limit notifications.{' '}
        <Link to="/admin/integrations">Configure QuickBooks</Link> credentials first.
      </p>
      <p>
        <button
          type="button"
          className="btn-link"
          onClick={async () => {
            const res = await fetch('/api/admin/time-tracker/time-entries/export', {
              headers: { Authorization: `Bearer ${getToken()}` },
            });
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'time-entries.csv';
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          Export time entries (CSV)
        </button>
      </p>
      {message ? <p className="ok">{message}</p> : null}
      {error ? <p className="err">{error}</p> : null}

      <div className="row-actions" style={{ marginBottom: '1rem' }} data-tour="time-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            className={tab === t ? 'btn-primary' : 'btn-secondary'}
            onClick={() => setTab(t)}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {tab === 'clients' ? (
        <ClientsTab
          clients={
            nearLimitOnly
              ? clients.filter(
                  (c) =>
                    Number(c.time_limit) > 0 &&
                    Number(c.current_time) >= Number(c.time_limit) * 0.85,
                )
              : clients
          }
          nearLimitOnly={nearLimitOnly}
          loading={loadingClients}
          qbStatus={qbStatus}
          syncing={syncing}
          onConnect={connectQuickBooks}
          onSyncCustomers={syncCustomers}
          onSyncAllTime={syncAllTime}
          onSaveLimit={saveClientLimit}
          onReset={resetClientTime}
          onToggleNotifications={toggleNotifications}
          onRunJob={runJob}
          onShowHistory={setTimeHistoryClient}
        />
      ) : null}

      <ClientTimeHistoryModal client={timeHistoryClient} onClose={() => setTimeHistoryClient(null)} />

      {tab === 'team' ? (
        <TeamSyncTab
          data={teamSync}
          loading={loadingTeamSync}
          syncing={syncing}
          onReload={loadTeamSync}
          onFlash={flash}
          onSetSyncing={setSyncing}
        />
      ) : null}

      {tab === 'notifications' && settings ? (
        <NotificationsTab
          settings={settings}
          setSettings={setSettings}
          mailgunOk={mailgunOk}
          onSave={saveSettings}
          onTestMailgun={testMailgun}
        />
      ) : null}

      {tab === 'history' ? (
        <HistoryTab
          history={history}
          summary={historySummary}
          onCleanup={cleanupHistory}
        />
      ) : null}
    </div>
  );
}

function ClientsTab({
  clients,
  nearLimitOnly = false,
  loading,
  qbStatus,
  syncing,
  onConnect,
  onSyncCustomers,
  onSyncAllTime,
  onSaveLimit,
  onReset,
  onToggleNotifications,
  onRunJob,
  onShowHistory,
}) {
  return (
    <div>
      {nearLimitOnly ? (
        <p className="muted small">Showing clients near their prepaid time limit (85%+ used).</p>
      ) : null}
      <div className="row-actions" style={{ marginBottom: '1rem' }}>
        <span className={`badge ${qbStatus.connected ? 'text-bg-success' : 'text-bg-secondary'}`}>
          QuickBooks: {qbStatus.connected ? 'connected' : 'not connected'}
        </span>
        {!qbStatus.connected ? (
          <button type="button" className="btn-primary" onClick={onConnect}>
            Connect QuickBooks
          </button>
        ) : null}
        <button type="button" className="btn-secondary" disabled={syncing || !qbStatus.connected} onClick={onSyncCustomers}>
          Sync clients from QB
        </button>
        <button type="button" className="btn-secondary" disabled={syncing || !qbStatus.connected} onClick={onSyncAllTime}>
          Sync all time
        </button>
        <button
          type="button"
          className="btn-secondary"
          disabled={syncing}
          onClick={() => onRunJob('/api/admin/time-tracker/scheduler/trigger-auto-reset', 'Auto-reset check done')}
        >
          Run auto-reset
        </button>
        <button
          type="button"
          className="btn-secondary"
          disabled={syncing}
          onClick={() =>
            onRunJob('/api/admin/time-tracker/scheduler/trigger-notification-check', 'Notification check done')
          }
        >
          Check limits & notify
        </button>
      </div>

      {loading ? <p className="muted">Loading…</p> : null}
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Notify</th>
              <th>Used / limit (h)</th>
              <th>Auto reset</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <ClientRow
                key={c.id}
                client={c}
                onSaveLimit={onSaveLimit}
                onReset={onReset}
                onToggleNotifications={onToggleNotifications}
                onShowHistory={onShowHistory}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ClientRow({ client, onSaveLimit, onReset, onToggleNotifications, onShowHistory }) {
  const [limit, setLimit] = useState(String(client.time_limit ?? 7));
  const [autoReset, setAutoReset] = useState(client.auto_reset_date ?? '01-01');
  const remaining = Number(client.time_limit ?? 0) - Number(client.current_time ?? 0);

  return (
    <tr>
      <td>
        <strong>{clientLabel(client)}</strong>
        {client.email ? <p className="muted small" style={{ margin: 0 }}>{client.email}</p> : null}
      </td>
      <td>
        <input
          type="checkbox"
          checked={!!client.notifications_enabled}
          onChange={(e) => onToggleNotifications(client, e.target.checked)}
        />
      </td>
      <td>
        {Number(client.current_time ?? 0).toFixed(1)} / {Number(client.time_limit ?? 0).toFixed(1)}
        <span className={`small ${remaining <= 0 ? 'err' : 'muted'}`}> ({remaining.toFixed(1)} left)</span>
      </td>
      <td>
        <input
          type="text"
          className="form-control form-control-sm"
          style={{ width: '5rem' }}
          value={autoReset}
          onChange={(e) => setAutoReset(e.target.value)}
          placeholder="MM-DD"
        />
        <input
          type="number"
          min="0"
          step="0.5"
          className="form-control form-control-sm d-inline-block ms-1"
          style={{ width: '4.5rem' }}
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
        />
      </td>
      <td className="row-actions">
        <button type="button" className="btn-link" onClick={() => onSaveLimit(client, limit, autoReset)}>
          Save
        </button>
        <button type="button" className="btn-link" onClick={() => onReset(client)}>
          Reset time
        </button>
        <button type="button" className="btn-link" onClick={() => onShowHistory(client)}>
          History
        </button>
      </td>
    </tr>
  );
}

function ClientTimeHistoryModal({ client, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!client) {
      setData(null);
      setError('');
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setData(null);

    apiJson(`/api/admin/time-tracker/clients/${client.id}/time-history`)
      .then((res) => {
        if (!cancelled) {
          setData(res);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [client]);

  const periodNote = data?.client?.time_reset_at
    ? `Since reset on ${formatDate(data.client.time_reset_at)}`
    : 'All recorded time';

  return (
    <Modal
      open={!!client}
      title={client ? `Time history — ${clientLabel(client)}` : 'Time history'}
      description={data ? periodNote : undefined}
      onClose={onClose}
      size="lg"
    >
      {loading ? <p className="muted">Loading…</p> : null}
      {error ? <p className="err">{error}</p> : null}
      {!loading && !error && data ? (
        <div>
          <p className="muted" style={{ marginTop: 0 }}>
            Total: <strong>{Number(data.total_hours).toFixed(1)} h</strong>
            {data.total_entries ? ` · ${data.total_entries} entries` : null}
          </p>

          {data.summary_by_employee?.length ? (
            <div className="table-wrap" style={{ marginBottom: '1rem' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Hours</th>
                    <th>Entries</th>
                  </tr>
                </thead>
                <tbody>
                  {data.summary_by_employee.map((row) => (
                    <tr key={row.employee_id}>
                      <td>{row.employee_name}</td>
                      <td>{Number(row.total_hours).toFixed(1)}</td>
                      <td>{row.entries}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {data.entries?.length ? (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Employee</th>
                    <th>Project</th>
                    <th>Description</th>
                    <th>Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((entry) => (
                    <tr key={entry.id}>
                      <td>{formatDate(entry.entry_date)}</td>
                      <td>{entry.employee_name}</td>
                      <td>{entry.project || '—'}</td>
                      <td>{entry.description || '—'}</td>
                      <td>{Number(entry.duration ?? 0).toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted">No time entries for this period.</p>
          )}
        </div>
      ) : null}
    </Modal>
  );
}

function NotificationsTab({ settings, setSettings, mailgunOk, onSave, onTestMailgun }) {
  return (
    <form onSubmit={onSave} className="card-panel">
      <p className="muted">
        Mailgun: {mailgunOk ? 'configured' : 'not configured (set MAILGUN_DOMAIN and MAILGUN_SECRET in server env)'}
      </p>
      <FormInput
        label="Owner email"
        value={settings.owner_email || ''}
        onChange={(e) => setSettings({ ...settings, owner_email: e.target.value })}
        required
      />
      <FormInput
        label="Employee email subject"
        value={settings.employee_subject || ''}
        onChange={(e) => setSettings({ ...settings, employee_subject: e.target.value })}
      />
      <FormRichTextarea
        label="Employee template (HTML, use {{clientName}}, {{timeUsed}}, {{timeLimit}})"
        value={settings.employee_template || ''}
        onChange={(value) => setSettings({ ...settings, employee_template: value })}
      />
      <FormInput
        label="Client email subject"
        value={settings.client_subject || ''}
        onChange={(e) => setSettings({ ...settings, client_subject: e.target.value })}
      />
      <FormRichTextarea
        label="Client template (HTML)"
        value={settings.client_template || ''}
        onChange={(value) => setSettings({ ...settings, client_template: value })}
      />
      <div className="row-actions">
        <button type="submit" className="btn-primary">
          Save settings
        </button>
        <button type="button" className="btn-secondary" onClick={onTestMailgun}>
          Send test email
        </button>
      </div>
    </form>
  );
}

function HistoryTab({ history, summary, onCleanup }) {
  return (
    <div>
      <p className="muted">
        Total: {summary.total} · Sent: {summary.sent} · Failed: {summary.failed}
      </p>
      <button type="button" className="btn-secondary" style={{ marginBottom: '1rem' }} onClick={onCleanup}>
        Cleanup old (90 days)
      </button>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>To</th>
              <th>Subject</th>
              <th>Client</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(history.data || []).map((row) => (
              <tr key={row.id}>
                <td>{formatDateTime(row.created_at)}</td>
                <td>{row.recipient_email}</td>
                <td>{row.subject}</td>
                <td>{row.client_name || '—'}</td>
                <td>{row.send_status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TeamSyncTab({ data, loading, syncing, onReload, onFlash, onSetSyncing }) {
  const [linkEmployee, setLinkEmployee] = useState(null);
  const [createEmployee, setCreateEmployee] = useState(null);
  const [linkUserId, setLinkUserId] = useState('');
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    passwordConfirmation: '',
    sendInvite: true,
    role: 'team_member',
  });
  const [busy, setBusy] = useState(false);

  const unlinkedTeamMembers = (data.team_members || []).filter((member) => !member.quickbooks_employee_id);

  const runSync = async () => {
    onSetSyncing(true);
    try {
      const res = await apiJson('/api/admin/time-tracker/employees/sync', { method: 'POST' });
      const stats = res.stats;
      onFlash(
        `Team sync done: ${stats.linked} linked by email, ${stats.already_linked} already linked, ${stats.unlinked} still unlinked.`,
      );
      await onReload();
    } catch (e) {
      onFlash(e.message, true);
    } finally {
      onSetSyncing(false);
    }
  };

  const openLink = (employee) => {
    setLinkEmployee(employee);
    setLinkUserId(unlinkedTeamMembers[0]?.id ? String(unlinkedTeamMembers[0].id) : '');
  };

  const openCreate = (employee) => {
    setCreateEmployee(employee);
    setCreateForm({
      name: employee.name || '',
      email: employee.email || '',
      password: '',
      passwordConfirmation: '',
      sendInvite: true,
      role: 'team_member',
    });
  };

  const submitLink = async () => {
    if (!linkEmployee || !linkUserId) {
      return;
    }
    setBusy(true);
    try {
      await apiJson('/api/admin/time-tracker/employees/link', {
        method: 'PUT',
        body: {
          quickbooks_employee_id: linkEmployee.quickbooks_id,
          user_id: Number(linkUserId),
        },
      });
      onFlash(`Linked ${linkEmployee.name} to team member.`);
      setLinkEmployee(null);
      await onReload();
    } catch (e) {
      onFlash(e.message, true);
    } finally {
      setBusy(false);
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createEmployee) {
      return;
    }
    setBusy(true);
    try {
      await apiJson('/api/admin/time-tracker/employees/create-team-member', {
        method: 'POST',
        body: createForm.sendInvite
          ? {
              quickbooks_employee_id: createEmployee.quickbooks_id,
              name: createForm.name,
              email: createForm.email,
              role: createForm.role,
              send_invite: true,
            }
          : {
              quickbooks_employee_id: createEmployee.quickbooks_id,
              name: createForm.name,
              email: createForm.email,
              password: createForm.password,
              password_confirmation: createForm.passwordConfirmation,
              role: createForm.role,
            },
      });
      onFlash(`Created team member for ${createEmployee.name}.`);
      setCreateEmployee(null);
      await onReload();
    } catch (e) {
      onFlash(e.message, true);
    } finally {
      setBusy(false);
    }
  };

  const unlinkMember = async (member) => {
    if (!window.confirm(`Unlink ${member.name} from QuickBooks?`)) {
      return;
    }
    setBusy(true);
    try {
      await apiJson(`/api/admin/time-tracker/employees/link/${member.id}`, { method: 'DELETE' });
      onFlash(`Unlinked ${member.name}.`);
      await onReload();
    } catch (e) {
      onFlash(e.message, true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <p className="muted">
        Match QuickBooks time-tracker employees to portal team members. Auto-sync links by email when possible.
      </p>
      <div className="row-actions" style={{ marginBottom: '1rem' }}>
        <span className={`badge ${data.quickbooks_connected ? 'text-bg-success' : 'text-bg-secondary'}`}>
          QuickBooks: {data.quickbooks_connected ? 'connected' : 'not connected'}
        </span>
        <button
          type="button"
          className="btn-secondary"
          disabled={syncing || !data.quickbooks_connected}
          onClick={() => void runSync()}
        >
          Sync employees from QB
        </button>
        <button type="button" className="btn-link" onClick={() => void onReload()}>
          Refresh
        </button>
      </div>

      {loading ? <p className="muted">Loading…</p> : null}

      {!loading ? (
        <>
          <h3>QuickBooks employees</h3>
          {(data.employees || []).length === 0 ? (
            <p className="muted">No employees found. Connect QuickBooks and sync time first.</p>
          ) : (
            <div className="table-wrap" style={{ marginBottom: '1.5rem' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Source</th>
                    <th>Linked team member</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(data.employees || []).map((employee) => (
                    <tr key={employee.quickbooks_id}>
                      <td>{employee.name}</td>
                      <td>{employee.email || '—'}</td>
                      <td>{employee.source === 'quickbooks' ? 'QuickBooks' : 'Time entries'}</td>
                      <td>
                        {employee.linked_user ? (
                          <>
                            {employee.linked_user.name}
                            <span className="muted small"> ({employee.linked_user.email})</span>
                          </>
                        ) : (
                          <span className="muted">Not linked</span>
                        )}
                      </td>
                      <td className="row-actions">
                        {employee.linked_user ? null : (
                          <>
                            <button
                              type="button"
                              className="btn-link"
                              disabled={busy || unlinkedTeamMembers.length === 0}
                              onClick={() => openLink(employee)}
                            >
                              Link
                            </button>
                            <button type="button" className="btn-link" disabled={busy} onClick={() => openCreate(employee)}>
                              Create new
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3>Team members</h3>
          {(data.team_members || []).length === 0 ? (
            <p className="muted">No team members yet.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>QuickBooks link</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(data.team_members || []).map((member) => {
                    const linkedEmployee = (data.employees || []).find(
                      (employee) => employee.linked_user?.id === member.id,
                    );
                    return (
                      <tr key={member.id}>
                        <td>{member.name}</td>
                        <td>{member.email}</td>
                        <td>{member.role === 'team_member' ? 'Team member' : 'Team admin'}</td>
                        <td>{linkedEmployee ? linkedEmployee.name : member.quickbooks_employee_id || '—'}</td>
                        <td className="row-actions">
                          {member.quickbooks_employee_id ? (
                            <button type="button" className="btn-link" disabled={busy} onClick={() => void unlinkMember(member)}>
                              Unlink
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}

      <Modal
        open={!!linkEmployee}
        title={linkEmployee ? `Link ${linkEmployee.name}` : 'Link employee'}
        description="Associate this QuickBooks employee with an existing team member."
        onClose={() => setLinkEmployee(null)}
      >
        <label className="field">
          <span>Team member</span>
          <SearchableSelect value={linkUserId} onChange={(e) => setLinkUserId(e.target.value)}>
            <option value="">Select team member…</option>
            {unlinkedTeamMembers.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name} ({member.email})
              </option>
            ))}
          </SearchableSelect>
        </label>
        <div className="row-actions" style={{ marginTop: '1rem' }}>
          <button type="button" className="btn-secondary" onClick={() => setLinkEmployee(null)}>
            Cancel
          </button>
          <button type="button" className="btn-primary" disabled={busy || !linkUserId} onClick={() => void submitLink()}>
            {busy ? 'Saving…' : 'Link'}
          </button>
        </div>
      </Modal>

      <Modal
        open={!!createEmployee}
        title={createEmployee ? `Create team member — ${createEmployee.name}` : 'Create team member'}
        description="Create a new portal team account linked to this QuickBooks employee."
        onClose={() => setCreateEmployee(null)}
      >
        <form onSubmit={submitCreate} className="stack">
          <FormInput
            label="Name"
            value={createForm.name}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
            required
          />
          <FormInput
            label="Email"
            type="email"
            value={createForm.email}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, email: e.target.value }))}
            required
          />
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={createForm.sendInvite}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, sendInvite: e.target.checked }))}
            />
            <span>Send a link so they can create their own password</span>
          </label>
          {!createForm.sendInvite ? (
            <>
              <FormInput
                label="Password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, password: e.target.value }))}
                required
              />
              <FormInput
                label="Confirm password"
                type="password"
                value={createForm.passwordConfirmation}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, passwordConfirmation: e.target.value }))}
                required
              />
            </>
          ) : null}
          <label className="field">
            <span>Role</span>
            <SearchableSelect
              value={createForm.role}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, role: e.target.value }))}
            >
              <option value="team_member">Team member</option>
              <option value="manager">Team admin</option>
            </SearchableSelect>
          </label>
          <div className="row-actions">
            <button type="button" className="btn-secondary" onClick={() => setCreateEmployee(null)}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create team member'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
